import { Container, Graphics, ColorMatrixFilter, Rectangle } from "pixi.js";
import type { AvatarConfig, HouseData, CityAsset, ResidentState } from "../api/types";
import { CityApi } from "../api/client";
import { IsoProjection } from "../core/projection";
import { createAssetSprite, setAssetSpriteFrame, type AssetSpriteView } from "../houses/AssetSprite";
import { routePosition,type RoutePosition } from './residentMotion';
import { AVATAR_BODY_HEIGHT,avatarFrame,resolveAvatarAppearance } from './avatarAppearance';
import {createCharacterSprite,CUSTOM_CHARACTER_ASSET} from './CustomCharacterSprite';
import {characterKey} from './characterAppearance';
export {routePosition} from './residentMotion';

type Actor = { house:HouseData; view:Container; sprite?:AssetSpriteView; appearance?:CityAsset; heading:number; legs?:[Graphics,Graphics]; arms?:[Graphics,Graphics]; state?:ResidentState; assetId:string; grey?:ColorMatrixFilter; onlineView?:Container; offlineView?:Container; hitArea?:Rectangle; x:number; y:number; };
export class ResidentLayer extends Container {
  private actors = new Map<string,Actor>();
  private readonly installs=new Map<string,number>();
  private timer = 0;
  private busy = false;
  private disposed = false;
  private suspended = false;

  /** Avoid polling presence while the city is covered or the app is hidden. */
  setSuspended(value:boolean):void {
    if(this.suspended===value)return;
    this.suspended=value;
    if(!value&&!this.disposed)void this.refresh();
  }
  private offset = 0;
  private lastRefresh = 0;
  private readonly visibility = () => { if (!document.hidden) void this.refresh(); };
  constructor(private api:CityApi, private projection:IsoProjection, private assets:CityAsset[], private houses:HouseData[], private select:(house:HouseData)=>void, private authenticated:boolean) {super(); this.sortableChildren=true;}
  async start():Promise<void> {
    for (const house of this.houses) {if(this.disposed)return;await this.install(house);}
    if(this.disposed)return;
    document.addEventListener("visibilitychange",this.visibility);
    this.update(Date.now());
    await this.refresh();
    this.update(Date.now());
    this.timer=window.setInterval(()=>void this.refresh(),20000);
  }
  private async install(house:HouseData):Promise<void> {
    const cfg=house.avatar ?? {enabled:true,assetId:"resident-blue",speed:1,points:[{x:50,y:50,wait:0}]};
    const revision=(this.installs.get(house.externalId)??0)+1;this.installs.set(house.externalId,revision);
    if (!cfg.enabled) {const old=this.actors.get(house.externalId);old?.view.destroy({children:true});this.actors.delete(house.externalId);return;}
    const renderKey=cfg.appearance?characterKey(cfg.appearance):cfg.assetId;
    const existing=this.actors.get(house.externalId); if (existing?.assetId===renderKey) {existing.house=house;return;}

    const view=new Container(); const actor:Actor={house,view,assetId:renderKey,heading:existing?.heading??Math.PI/4,x:existing?.x??50,y:existing?.y??50,state:existing?.state??house.presence};
    const shadow=new Graphics().ellipse(0,0,15,5).fill({color:0x101827,alpha:.25}); view.addChild(shadow);
    const asset=cfg.appearance?CUSTOM_CHARACTER_ASSET:resolveAvatarAppearance(cfg.assetId)??this.assets.find(a=>a.assetId===cfg.assetId);
    if (cfg.appearance||asset?.sourceUrl) {
      try {
        const sprite=cfg.appearance?await createCharacterSprite(cfg.appearance):await createAssetSprite({assetId:asset!.assetId,x:0,y:0,scale:1},asset!,asset!.sourceUrl!,this.projection);
        if (sprite.height>0) sprite.scale.set(AVATAR_BODY_HEIGHT/(asset!.avatarAnimation?.bodyHeight??sprite.height));
        actor.sprite=sprite;actor.appearance=asset;view.addChild(sprite);
        if(asset!.avatarAnimation)setAssetSpriteFrame(sprite,avatarFrame(asset!,actor.heading,false,0));
      } catch {this.drawDefault(actor);}
    } else this.drawDefault(actor);
    if(this.disposed||this.installs.get(house.externalId)!==revision){view.destroy({children:true});return;}
    const previous=this.actors.get(house.externalId);previous?.view.destroy({children:true});
    actor.hitArea=new Rectangle();view.hitArea=actor.hitArea;
    view.eventMode="static";view.cursor="pointer";view.on("pointertap",event=>{event.stopPropagation();this.select(actor.house);});
    if (actor.sprite) { const grey=new ColorMatrixFilter();grey.desaturate();actor.grey=grey; }
    this.showPresence(actor,!!actor.state?.online);
    this.addChild(view);this.actors.set(house.externalId,actor);
  }
  private showPresence(actor:Actor, online:boolean):void {
    if (actor.sprite) actor.view.filters=!online&&actor.grey?[actor.grey]:[];
    if (actor.onlineView) actor.onlineView.visible=online;
    if (actor.offlineView) actor.offlineView.visible=!online;
  }
  private drawDefault(actor:Actor):void {
    const suit=actor.assetId==="resident-violet"?0x9a77ff:actor.assetId==="resident-green"?0x39bfa5:0x559bff;
    for (const online of [true,false]) {
      const character=new Container();actor.view.addChild(character);
      const color=online?suit:0x969696;
      const legs:[Graphics,Graphics]=[new Graphics(),new Graphics()];
      legs.forEach((leg,i)=>{leg.roundRect(-3,0,6,12,2).fill(online?0x1c2c48:0x2b2b2b);leg.position.set(i?4:-4,-12);character.addChild(leg);});
      character.addChild(new Graphics().roundRect(-8,-31,16,20,6).fill(color));
      const arms:[Graphics,Graphics]=[new Graphics(),new Graphics()];
      arms.forEach((arm,i)=>{arm.roundRect(-2,0,4,13,2).fill(color);arm.position.set(i?9:-9,-28);character.addChild(arm);});
      character.addChild(new Graphics().circle(0,-39,9).fill(online?0xe8efff:0xefefef).roundRect(-7,-42,14,6,3).fill(online?0x14253e:0x252525).circle(-3,-39,1.4).fill(online?0x73d9ff:0xc9c9c9).circle(3,-39,1.4).fill(online?0x73d9ff:0xc9c9c9));
      if (online) {actor.onlineView=character;actor.legs=legs;actor.arms=arms;}
      else actor.offlineView=character;
    }
  }
  update(now:number):void {
    for (const actor of this.actors.values()) {
      const bounds=actor.house.placement.parcelBounds; if (!bounds) continue;
      const cfg=actor.house.avatar ?? {enabled:true,assetId:"resident-blue",speed:1,points:[{x:50,y:50,wait:0}]};
      const state=actor.state;
      if (state?.online && now-this.lastRefresh>95000) {
        state.online=false; state.position={x:actor.x,y:actor.y};
        this.showPresence(actor,false);
      }
      const p:RoutePosition=state?.online ? routePosition(cfg,now/1000+this.offset-state.since,bounds.width,bounds.depth):{...(state?.position??cfg.points[0]),moving:false};
      if(p.heading!==undefined)actor.heading=p.heading;
      actor.x=p.x;actor.y=p.y;
      const zoom=Math.max(.01,Math.abs(this.parent?.scale.x??1));
      const height=actor.sprite?AVATAR_BODY_HEIGHT:52;
      const visualScale=actor.sprite?1:AVATAR_BODY_HEIGHT/52;
      actor.view.scale.set(visualScale);
      if (actor.hitArea) {
        const width=Math.max(32,40/(zoom*visualScale)),targetHeight=Math.max(height+5,40/(zoom*visualScale));
        actor.hitArea.x=-width/2;actor.hitArea.y=-targetHeight;actor.hitArea.width=width;actor.hitArea.height=targetHeight+5;
      }
      const point=this.projection.worldToScreen((bounds.centerX??actor.house.placement.x)+(p.x/100-.5)*bounds.width,(bounds.centerY??actor.house.placement.y)+(p.y/100-.5)*bounds.depth);
      actor.view.position.set(point.x,point.y);actor.view.zIndex=point.y;
      const swing=p.moving?Math.sin(now/95)*.3:0;
      actor.legs?.forEach((leg,i)=>leg.rotation=i?swing:-swing);
      actor.arms?.forEach((arm,i)=>arm.rotation=i?-swing:swing);
      if (actor.sprite) setAssetSpriteFrame(actor.sprite,actor.appearance?.avatarAnimation
        ?avatarFrame(actor.appearance,actor.heading,p.moving,now/1000+this.offset-(state?.since??0),cfg.speed)
        :p.moving?Math.floor(now/100):0);
    }
  }
  private async refresh():Promise<void> {
    if (this.busy||this.disposed||document.hidden||this.suspended) return;
    this.busy=true;
    try {
      const positions:Record<string,{x:number;y:number}>={};
      for (const actor of this.actors.values()) if (actor.house.canManage) positions[actor.house.externalId]={x:actor.x,y:actor.y};
      const active=this.authenticated && document.visibilityState === "visible";
      const result=await this.api.residents(active?positions:undefined);
      if (this.disposed) return;
      this.lastRefresh=Date.now();
      this.offset=result.serverTime-this.lastRefresh/1000;
      for (const state of result.residents) {
        let actor=this.actors.get(state.externalId);
        const house=this.houses.find(h=>h.externalId===state.externalId);if (!house) continue;
        house.avatar=state.avatar;house.presence=state;
        if (!state.avatar.enabled) {await this.install(house);continue;}
        await this.install(house);actor=this.actors.get(state.externalId); if (!actor) continue;
        actor.state=state;
        this.showPresence(actor,state.online);
      }
    } catch (error) { console.warn("City resident state could not be refreshed", error); }
    finally {this.busy=false;}
  }
  async changed(house:HouseData):Promise<void> {await this.install(house);await this.refresh();}
  override destroy():void {this.disposed=true;window.clearInterval(this.timer);document.removeEventListener("visibilitychange",this.visibility);super.destroy({children:true});}
}
